import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Loader2, RefreshCw, BarChart3, Activity, DollarSign, 
  Target, Building2, Package, Factory, Users
} from 'lucide-react';
import { dashboardApiService, ExecutiveDashboardData } from '../services/dashboardApi';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

export function ExecutiveDashboard() {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<ExecutiveDashboardData | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    loadDashboardData();
  }, [selectedBranch, startDate, endDate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardApiService.getExecutiveDashboard(
        startDate,
        endDate,
        selectedBranch || undefined
      );
      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        toast.error(response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      // console.error('Error loading executive dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const formatChange = (value: number): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">No data available</p>
          <Button onClick={loadDashboardData} variant="outline" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const COLORS = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <BarChart3 className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Executive Dashboard</CardTitle>
                  <p className="text-gray-600 text-sm mt-1">
                    {dashboardData.lastUpdated && `Last updated: ${new Date(dashboardData.lastUpdated).toLocaleString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label htmlFor="startDate" className="text-white text-sm">From:</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40 bg-white/20 border-white/30 text-white placeholder:text-white/70"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="endDate" className="text-white text-sm">To:</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40 bg-white/20 border-white/30 text-white placeholder:text-white/70"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDashboardData}
                  disabled={loading}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Purchase Spend (YTD)</p>
                  <p className="text-2xl font-bold text-primary-600 mt-1">
                    {formatCurrency(dashboardData.executiveSummaryCards.totalPurchaseSpend.ytd)}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {dashboardData.executiveSummaryCards.totalPurchaseSpend.change >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-xs ${dashboardData.executiveSummaryCards.totalPurchaseSpend.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatChange(dashboardData.executiveSummaryCards.totalPurchaseSpend.change)}
                    </span>
                  </div>
                </div>
                <DollarSign className="h-8 w-8 text-primary-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Sales Revenue (YTD)</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatCurrency(dashboardData.executiveSummaryCards.totalSalesRevenue.ytd)}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {dashboardData.executiveSummaryCards.totalSalesRevenue.change >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-xs ${dashboardData.executiveSummaryCards.totalSalesRevenue.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatChange(dashboardData.executiveSummaryCards.totalSalesRevenue.change)}
                    </span>
                  </div>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Inventory Value</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">
                    {formatCurrency(dashboardData.executiveSummaryCards.totalInventoryValue)}
                  </p>
                </div>
                <Package className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">WIP Value</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">
                    {formatCurrency(dashboardData.executiveSummaryCards.totalWIPValue)}
                  </p>
                </div>
                <Factory className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Supplier Performance</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    {dashboardData.executiveSummaryCards.averageSupplierPerformanceScore.toFixed(1)}
                  </p>
                </div>
                <Users className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Production Efficiency</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">
                    {formatPercentage(dashboardData.executiveSummaryCards.overallProductionEfficiency)}
                  </p>
                </div>
                <Target className="h-8 w-8 text-indigo-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Overview Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Purchase Spend Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData.purchaseSpendAnalysis.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="poValue" stroke="#ef4444" name="PO Value" />
                  <Line type="monotone" dataKey="grnValue" stroke="#3b82f6" name="GRN Value" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Sales Revenue Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.salesRevenueAnalysis.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="orderValue" fill="#10b981" name="Order Value" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Branch Performance Comparison */}
        <Card className="border-primary-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Branch Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.branchPerformance.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No branch performance data</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Branch</TableHead>
                    <TableHead className="text-right">Purchase Spend</TableHead>
                    <TableHead className="text-right">Sales Revenue</TableHead>
                    <TableHead className="text-right">Inventory Value</TableHead>
                    <TableHead className="text-right">WIP Value</TableHead>
                    <TableHead className="text-right">Performance Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.branchPerformance.map((branch) => (
                    <TableRow key={branch.branchName}>
                      <TableCell className="font-medium">{branch.branchName}</TableCell>
                      <TableCell className="text-right">{formatCurrency(branch.purchaseSpend)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(branch.salesRevenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(branch.inventoryValue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(branch.wipValue)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={branch.performanceScore >= 80 ? "outline" : "destructive"}>
                          {branch.performanceScore.toFixed(1)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Strategic KPIs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Purchase Efficiency</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">PO Fulfillment Rate</span>
                  <span className="text-lg font-bold">{formatPercentage(dashboardData.strategicKPIs.purchaseEfficiency.poFulfillmentRate)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">On-Time Delivery %</span>
                  <span className="text-lg font-bold">{formatPercentage(dashboardData.strategicKPIs.purchaseEfficiency.onTimeDeliveryPercentage)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Average Delivery Time</span>
                  <span className="text-lg font-bold">{dashboardData.strategicKPIs.purchaseEfficiency.averageDeliveryTime.toFixed(1)} days</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Sales Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Order Fulfillment Rate</span>
                  <span className="text-lg font-bold">{formatPercentage(dashboardData.strategicKPIs.salesPerformance.orderFulfillmentRate)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Average Order Value</span>
                  <span className="text-lg font-bold">{formatCurrency(dashboardData.strategicKPIs.salesPerformance.averageOrderValue)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Customer Satisfaction</span>
                  <span className="text-lg font-bold">{formatPercentage(dashboardData.strategicKPIs.salesPerformance.customerSatisfaction)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risk Indicators */}
        <Card className="border-primary-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Risk Indicators</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="p-3 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Low Performance Suppliers</p>
                <p className="text-2xl font-bold text-red-600">{dashboardData.riskIndicators.lowPerformanceSuppliers.length}</p>
              </div>
              <div className="p-3 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">High Rejection Items</p>
                <p className="text-2xl font-bold text-orange-600">{dashboardData.riskIndicators.highRejectionRateItems.length}</p>
              </div>
              <div className="p-3 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Overdue Deliveries</p>
                <p className="text-2xl font-bold text-yellow-600">{dashboardData.riskIndicators.overdueDeliveries}</p>
              </div>
              <div className="p-3 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Overdue Work Orders</p>
                <p className="text-2xl font-bold text-purple-600">{dashboardData.riskIndicators.overdueWorkOrders}</p>
              </div>
              <div className="p-3 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-1">Critical Low Stock</p>
                <p className="text-2xl font-bold text-pink-600">{dashboardData.riskIndicators.criticalLowStockItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

